import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useAuth } from "./AuthContext";
import { toastHandler } from "@/lib/ToastHandler";
import {
  Permission,
  ROLE_PERMISSIONS,
  ResourceAccess,
  PermissionContextType,
} from "@/types/permissions";

// Create the context
const PermissionContext = createContext<PermissionContextType | undefined>(
  undefined,
);

// Permission provider component
export function PermissionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { userRole, supabaseInfo, isAuthenticated } = useAuth();
  const [resourceAccess, setResourceAccess] = useState<ResourceAccess>({
    brandIds: [],
    branchIds: [],
    canAccessAll: false,
  });

  // Memoizar los permisos para evitar recalcular constantemente
  const permissions = useMemo(() => {
    if (!userRole) return [];
    return ROLE_PERMISSIONS[userRole] || [];
  }, [userRole]);

  // Estabilizar las funciones para evitar re-renders
  const contextFunctions = useMemo(
    () => ({
      hasPermission: (permission: Permission): boolean => {
        return permissions.includes(permission);
      },
      hasBrandAccess: (brandId: number): boolean => {
        if (resourceAccess.canAccessAll) return true;
        return resourceAccess.brandIds.includes(brandId);
      },
      hasBranchAccess: (branchId: number): boolean => {
        if (resourceAccess.canAccessAll) return true;
        return resourceAccess.branchIds.includes(branchId);
      },
      hasBranchAccessByBrand: (brandId: number): boolean => {
        if (resourceAccess.canAccessAll) return true;
        return resourceAccess.brandIds.includes(brandId);
      },
      canAccessResource: (
        resourceType: "business" | "branch",
        resourceId: number,
      ): boolean => {
        if (resourceType === "business") {
          if (resourceAccess.canAccessAll) return true;
          return resourceAccess.brandIds.includes(resourceId);
        }
        if (resourceAccess.canAccessAll) return true;
        return resourceAccess.branchIds.includes(resourceId);
      },
      getAccessibleBrands: (): number[] => {
        if (resourceAccess.canAccessAll) return [];
        return resourceAccess.brandIds;
      },
      getAccessibleBranches: (): number[] => {
        if (resourceAccess.canAccessAll) return [];
        return resourceAccess.branchIds;
      },
      getPermissionMatrix: () => ROLE_PERMISSIONS,
    }),
    [permissions, resourceAccess],
  );

  const contextValue: PermissionContextType = useMemo(
    () => ({
      permissions,
      resourceAccess,
      ...contextFunctions,
    }),
    [permissions, resourceAccess, contextFunctions],
  );

  // Load user's resource access when authenticated
  useEffect(() => {
    if (!isAuthenticated || !supabaseInfo) return;

    const loadResourceAccess = async () => {
      try {
        // Admin users have access to everything
        if (userRole === "admin") {
          setResourceAccess({
            brandIds: [],
            branchIds: [],
            canAccessAll: true,
          });
          return;
        }

        const brandIds = supabaseInfo.brandIds || [];
        const branchIds = supabaseInfo.branchIds || [];

        setResourceAccess({
          brandIds,
          branchIds,
          canAccessAll: false,
        });
      } catch (error) {
        toastHandler({
          description:
            "No se pudieron cargar los permisos. Algunas funciones pueden no estar disponibles.",
          variant: "warning",
          title: "Permisos",
        });
        setResourceAccess({
          brandIds: [],
          branchIds: [],
          canAccessAll: false,
        });
      }
    };

    loadResourceAccess();
  }, [isAuthenticated, supabaseInfo, userRole]);

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
}

// Hook to use the permission context
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
}
